import apiClient from "../lib/api-client";

export interface LoyaltyProgram {
  id: string;
  name: string;
  description?: string;
  pointsPerAmount: number;
  amountPerPoint: number;
  minRedeemPoints?: number;
  maxRedeemPercent?: number;
  expiryDays?: number;
  isActive: boolean;
  tiers?: LoyaltyTier[];
  _count?: {
    tiers: number;
    customerLoyalty: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyTier {
  id: string;
  programId: string;
  name: string;
  minPoints: number;
  maxPoints?: number;
  multiplier: number;
  benefits?: any;
}

export interface CustomerLoyalty {
  id: string;
  customerId: string;
  programId: string;
  currentTierId?: string;
  totalPoints: number;
  currentPoints: number;
  redeemedPoints: number;
  program?: LoyaltyProgram;
  currentTier?: LoyaltyTier;
  customer?: {
    id: string;
    name: string;
    phone: string;
  };
}

export interface CreateLoyaltyProgramData {
  name: string;
  description?: string;
  pointsPerAmount: number;
  amountPerPoint: number;
  minRedeemPoints?: number;
  maxRedeemPercent?: number;
  expiryDays?: number;
}

export interface LoyaltyProgramsParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface LoyaltyProgramsResponse {
  data: LoyaltyProgram[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export const loyaltyProgramService = {
  getAll: async (
    params?: LoyaltyProgramsParams,
  ): Promise<LoyaltyProgramsResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.limit) searchParams.set("limit", params.limit.toString());
    if (params?.search) searchParams.set("search", params.search);
    if (params?.isActive !== undefined)
      searchParams.set("isActive", params.isActive.toString());

    const response = await apiClient.get(
      `/loyalty-programs?${searchParams.toString()}`,
    );
    return response.data;
  },

  getById: async (id: string): Promise<LoyaltyProgram> => {
    const response = await apiClient.get(`/loyalty-programs/${id}`);
    return response.data;
  },

  getActive: async (): Promise<LoyaltyProgram | null> => {
    const response = await apiClient.get("/loyalty-programs/active");
    return response.data;
  },

  create: async (data: CreateLoyaltyProgramData): Promise<LoyaltyProgram> => {
    const response = await apiClient.post("/loyalty-programs", data);
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<CreateLoyaltyProgramData>,
  ): Promise<LoyaltyProgram> => {
    const response = await apiClient.patch(`/loyalty-programs/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/loyalty-programs/${id}`);
  },

  addTier: async (
    programId: string,
    tier: Omit<LoyaltyTier, "id" | "programId">,
  ): Promise<LoyaltyTier> => {
    const response = await apiClient.post(
      `/loyalty-programs/${programId}/tiers`,
      tier,
    );
    return response.data;
  },

  getCustomerLoyalty: async (
    customerId: string,
  ): Promise<CustomerLoyalty | null> => {
    const response = await apiClient.get(
      `/loyalty-programs/customer/${customerId}`,
    );
    return response.data;
  },

  enrollCustomer: async (
    customerId: string,
    programId: string,
  ): Promise<CustomerLoyalty> => {
    const response = await apiClient.post(
      `/loyalty-programs/customer/${customerId}/enroll`,
      {
        programId,
      },
    );
    return response.data;
  },

  addPoints: async (
    customerId: string,
    points: number,
    transactionRef?: string,
    description?: string,
  ): Promise<any> => {
    const response = await apiClient.post(
      `/loyalty-programs/customer/${customerId}/add-points`,
      {
        points,
        transactionRef,
        description,
      },
    );
    return response.data;
  },

  redeemPoints: async (
    customerId: string,
    points: number,
    transactionRef?: string,
    description?: string,
  ): Promise<any> => {
    const response = await apiClient.post(
      `/loyalty-programs/customer/${customerId}/redeem`,
      {
        points,
        transactionRef,
        description,
      },
    );
    return response.data;
  },
};
